// Checks for admin edits to the registry, shared by the API (authoritative) and the editor UI (early feedback).

import { ENTITY_SLOTS } from '../design/tokens';
import { groupsForType } from './resolve';
import {
  BLOCK_BUILTINS,
  BLOCK_LAYOUTS,
  type FieldDef,
  isColumnType,
  isFieldType,
  isKey,
  type SchemaSnapshot,
  type TableColumn,
  type TemplateBlock,
} from './types';

export const isColorSlot = (v: unknown): v is number | null =>
  v === null || (Number.isInteger(v) && (v as number) >= 1 && (v as number) <= ENTITY_SLOTS);

/** Problems with a table's columns: keys, types, list references. */
export function checkColumns(columns: unknown, lists: SchemaSnapshot['lists']): string[] {
  if (!Array.isArray(columns) || !columns.length) return ['A table needs at least one column.'];
  const errors: string[] = [];
  const keys = new Set<string>();
  for (const c of columns as Partial<TableColumn>[]) {
    const name = c?.label || c?.key || '?';
    if (!isKey(c?.key)) errors.push(`Column "${name}": the key must be lowercase letters, digits and _.`);
    else if (keys.has(c.key)) errors.push(`Column "${name}": two columns share the key ${c.key}.`);
    else keys.add(c.key);
    if (!c?.label?.trim()) errors.push(`Column "${name}": a label is required.`);
    if (!isColumnType(c?.type)) errors.push(`Column "${name}": unknown type.`);
    if ((c?.type === 'enum' || c?.type === 'multi_enum') && !lists.some(l => l.key === c.enumList))
      errors.push(`Column "${name}": pick the value list it uses.`);
  }
  return errors;
}

type FieldInput = Partial<Pick<FieldDef, 'label' | 'dataType' | 'enumList' | 'ratingScale' | 'columns'>>;

/** Problems with a field definition (type-specific settings). */
export function checkFieldDef(f: FieldInput, lists: SchemaSnapshot['lists']): string[] {
  const errors: string[] = [];
  if (!f.label?.trim()) errors.push('A label is required.');
  if (!isFieldType(f.dataType)) return [...errors, 'Pick a data type.'];
  if ((f.dataType === 'enum' || f.dataType === 'multi_enum') && !lists.some(l => l.key === f.enumList))
    errors.push('Pick the value list this field uses.');
  if (f.dataType === 'rating' && !lists.some(l => l.key === f.ratingScale)) errors.push('Pick the rating scale this field uses.');
  if (f.dataType === 'table') errors.push(...checkColumns(f.columns, lists));
  return errors;
}

/** Problems with a template's blocks for an entity type (keys, layouts, groups, import patterns). */
export function checkTemplateBlocks(blocks: unknown, snap: SchemaSnapshot, entityTypeId: string): string[] {
  if (!Array.isArray(blocks)) return ['Blocks must be a list.'];
  const errors: string[] = [];
  const groups = new Set(groupsForType(snap, entityTypeId, { includeArchived: true }).map(g => g.key));
  const keys = new Set<string>();
  for (const b of blocks as Partial<TemplateBlock>[]) {
    const name = b?.label || b?.key || '?';
    if (!isKey(b?.key)) errors.push(`Block "${name}": the key must be lowercase letters, digits and _.`);
    else if (keys.has(b.key)) errors.push(`Block "${name}": two blocks share the key ${b.key}.`);
    else keys.add(b.key);
    if (!b?.label?.trim()) errors.push(`Block "${name}": a label is required.`);
    if (!BLOCK_LAYOUTS.includes(b?.layout as never)) errors.push(`Block "${name}": unknown layout.`);
    if (typeof b?.order !== 'number' || !Number.isFinite(b.order)) errors.push(`Block "${name}": order must be a number.`);
    if (b?.group && !groups.has(b.group)) errors.push(`Block "${name}": this type has no field group "${b.group}".`);
    for (const x of b?.builtins ?? []) if (!BLOCK_BUILTINS.includes(x)) errors.push(`Block "${name}": unknown built-in "${x}".`);
    for (const p of b?.import?.headingPatterns ?? []) {
      if (typeof p !== 'string' || p.length > 200) errors.push(`Block "${name}": heading patterns must be text up to 200 characters.`);
      else
        try {
          new RegExp(p, 'i');
        } catch {
          errors.push(`Block "${name}": the heading pattern "${p}" is not a valid pattern.`);
        }
    }
    for (const [k, v] of Object.entries(b?.import?.columns ?? {}))
      if (!Array.isArray(v) || v.some(x => typeof x !== 'string'))
        errors.push(`Block "${name}": column headings for ${k} must be a list of text.`);
  }
  return errors;
}
